import type { VersionUpdateMode } from '../components/modals/submodals/VersionUpdateModal/types';

/**
 * La escalera de insistencia del aviso de actualización.
 *
 * ─── La idea ───────────────────────────────────────────────────────────────
 *
 * Cada vez que el operario cierra el aviso, el siguiente llega antes. Los
 * cuatro silencios suman EXACTAMENTE 24 unidades, así que el quinto cierre y
 * el plazo de 24 unidades caen en el mismo sitio por construcción: no son dos
 * reglas que haya que cuadrar a mano, son la misma vista de dos formas.
 *
 * ─── Por qué "unidades" y no horas ─────────────────────────────────────────
 *
 * Una unidad es una hora en producción y un minuto cuando se prueba. Probar 24
 * horas de escalera en tiempo real no es viable, y con la proporción intacta
 * (1:60) la prueba recorre LA MISMA escalera que verá el operario, no una
 * aproximación.
 *
 * ─── Por qué este módulo es puro ───────────────────────────────────────────
 *
 * Sin React, sin Capacitor y sin tocar `localStorage`: recibe el estado y
 * devuelve decisiones. Es la parte con reglas y es lo que hay que poder probar
 * sin montar un WebView. Quien lee y escribe el estado es `updateNagStorage`.
 */

/** Lo que se recuerda de un aviso concreto. */
export interface NagState {
  /**
   * Identidad del update: la huella nativa cuando hace falta APK, la versión
   * web cuando es OTA. No es lo que se ENSEÑA (eso es `webVersion`), es lo que
   * distingue un aviso de otro.
   */
  version: string;
  /** Cuándo vio ESTA tablet esta versión por primera vez. */
  firstSeen: number;
  /** Cuántas veces la ha cerrado el operario. */
  dismissals: number;
}

/**
 * Cuánto se calla tras el cierre n.º 1, 2, 3 y 4. Suman 24, que es el plazo.
 * Cambiar estos números mueve también el plazo: mantener la suma.
 */
export const QUIET_STEPS_UNITS = [12, 6, 4, 2] as const;

/** Al quinto cierre ya no se puede aplazar. */
export const MAX_DISMISSALS = 5;

/** Plazo máximo desde `firstSeen`, en unidades. Igual a la suma de la escalera. */
export const DEADLINE_UNITS = QUIET_STEPS_UNITS.reduce((a, b) => a + b, 0);

/** Una unidad en producción. La prueba la sustituye por un minuto. */
export const DEFAULT_UNIT_MS = 60 * 60 * 1000;

/**
 * Cuánto callarse después del cierre número `dismissals` (empezando en 1).
 *
 * 🔴 Fuera de la tabla se mantiene el escalón MÁS CORTO, nunca cero. En el
 * camino OTA daría igual —pasado el quinto cierre manda el bloqueo, que se
 * comprueba antes que el silencio—, pero en el del APK el bloqueo no llega
 * nunca por diseño (ver `isBlocking`): con cero, el aviso reaparecería en cada
 * encendido de pantalla para siempre. Dejar al operario sin poder actualizar y
 * acosado es la otra forma de dejarlo sin tablet usable.
 *
 * Por abajo se protege igual: un contador corrupto en 0 o en negativo devuelve
 * el primer escalón en vez de dejar el silencio sin efecto.
 */
export function quietMsFor(dismissals: number, unitMs: number): number {
  const indice = Math.min(
    Math.max(dismissals - 1, 0),
    QUIET_STEPS_UNITS.length - 1
  );
  return QUIET_STEPS_UNITS[indice] * unitMs;
}

/**
 * ¿Sigue vigente el silencio?
 *
 * 🔴 El tope de arriba no es paranoia: si el reloj de la tablet salta hacia
 * atrás —cambio de hora, NTP, el operario tocando ajustes— un `quietUntil`
 * guardado antes queda a años vista y el aviso no volvería NUNCA. Más allá del
 * escalón más largo posible, se da por caducado.
 */
export function isQuiet(
  quietUntil: number | null,
  now: number,
  unitMs: number
): boolean {
  if (quietUntil === null) return false;
  if (quietUntil <= now) return false;
  const maxQuiet = Math.max(...QUIET_STEPS_UNITS) * unitMs;
  return quietUntil - now <= maxQuiet;
}

/** ¿El aviso deja de poder cerrarse? */
export function isBlocking({
  state,
  now,
  mandatory,
  unitMs,
  maxDismissals = MAX_DISMISSALS,
}: {
  state: NagState;
  now: number;
  /**
   * Ya no cambia el resultado: desde el 30-sep el APK bloquea igual que el
   * OTA. Se conserva para que el llamante declare el camino y por si vuelve a
   * hacer falta distinguirlos.
   */
  mode: VersionUpdateMode;
  mandatory: boolean;
  unitMs: number;
  /** Solo lo baja una build de prueba; ver `resolveMaxDismissals`. */
  maxDismissals?: number;
}): boolean {
  // La release marcada a mano manda sobre todo, incluido el APK: es la palanca
  // para el fallo que no admite seguir con la versión vieja.
  if (mandatory) return true;

  // El camino del APK escala igual que el OTA (decisión del 30-sep: al quinto
  // cierre o a las 24 h, también sin X). 🔴 El riesgo que antes lo excluía
  // —sin cobertura la descarga de varios MB falla y el operario se queda
  // encerrado— lo cubre el modal: tras un intento FALLIDO enseña la X
  // (`onEscape`), y el aviso vuelve bloqueante en la siguiente comprobación.

  // `>=` y no `===`: un estado manipulado o una carrera podrían dejar el
  // contador por encima, y eso no puede ser una vía para saltarse el bloqueo.
  if (state.dismissals >= maxDismissals) return true;

  // La red para la tablet que NUNCA llega a ver el aviso porque siempre hay un
  // tramo en curso o un modal del operario por encima: sin esto su contador no
  // avanzaría jamás y no actualizaría nunca.
  //
  // Un `firstSeen` en el futuro (reloj movido) da una resta negativa y no
  // bloquea, que es el lado seguro del error.
  return now - state.firstSeen >= DEADLINE_UNITS * unitMs;
}
